import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { LogOut, Users, MapPinned, CheckCircle2, Clapperboard } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MembersTable } from "@/components/admin/MembersTable";
import { BroadcastComposer } from "@/components/admin/BroadcastComposer";
import { BroadcastHistory } from "@/components/admin/BroadcastHistory";
import { VideosManager } from "@/components/admin/VideosManager";
import { DocumentsManager } from "@/components/admin/DocumentsManager";
import { MPsManager } from "@/components/admin/MPsManager";
import { LetterManager } from "@/components/admin/LetterManager";
import { EmailLog } from "@/components/admin/EmailLog";
import { MeetingsManager } from "@/components/admin/MeetingsManager";
import { BillsManager } from "@/components/admin/BillsManager";
import { CardsManager } from "@/components/admin/CardsManager";
import { EmailSettings } from "@/components/admin/EmailSettings";

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] p-5 flex items-center gap-4">
    <span className="w-11 h-11 rounded-xl bg-[#E2705A]/15 text-[#E2705A] flex items-center justify-center">
      <Icon className="w-5 h-5" />
    </span>
    <div>
      <p className="font-type text-2xl font-bold" data-testid={testId}>{value ?? "–"}</p>
      <p className="text-xs uppercase tracking-wider text-[#8FA58A]">{label}</p>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: () => api.get("/admin/stats").then((r) => r.data) });

  return (
    <div data-testid="admin-dashboard-container" className="min-h-screen bg-[#0F172A] text-[#F8FAFC]">
      <header className="border-b border-[#2C3E55] bg-[#121E2B]/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div>
            <p className="font-type font-bold text-lg leading-tight">R.O.A.N Admin</p>
            <p className="text-[11px] text-[#8FA58A]">{user?.email}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs text-[#8FA58A] hover:text-[#E2705A]" data-testid="admin-view-site-link">View site</Link>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#2C3E55] hover:border-[#E2705A] hover:text-[#E2705A] transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 space-y-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total F.E.R.Ms" value={stats?.total_members} testId="admin-stat-members" />
          <StatCard icon={MapPinned} label={`Electorates active / ${stats?.total_electorates ?? 150}`} value={stats?.electorates_covered} testId="admin-stat-electorates" />
          <StatCard icon={CheckCircle2} label="Full groups" value={stats?.full_groups} testId="admin-stat-full-groups" />
          <StatCard icon={Clapperboard} label="Active videos" value={stats?.active_videos} testId="admin-stat-videos" />
        </div>

        <Tabs defaultValue="members">
          <TabsList className="bg-[#1B2A41] border border-[#2C3E55] h-auto p-1 flex-wrap justify-start">
            <TabsTrigger value="members" data-testid="admin-tab-members" className="font-type data-[state=active]:bg-[#E2705A] data-[state=active]:text-white">Registrations</TabsTrigger>
            <TabsTrigger value="mps" data-testid="admin-tab-mps" className="font-type data-[state=active]:bg-[#E2705A] data-[state=active]:text-white">Federal members</TabsTrigger>
            <TabsTrigger value="emails" data-testid="admin-tab-emails" className="font-type data-[state=active]:bg-[#E2705A] data-[state=active]:text-white">Emails</TabsTrigger>
            <TabsTrigger value="meetings" data-testid="admin-tab-meetings" className="font-type data-[state=active]:bg-[#E2705A] data-[state=active]:text-white">Meetings</TabsTrigger>
            <TabsTrigger value="bills" data-testid="admin-tab-bills" className="font-type data-[state=active]:bg-[#E2705A] data-[state=active]:text-white">Bill tracker</TabsTrigger>
            <TabsTrigger value="videos" data-testid="admin-tab-videos" className="font-type data-[state=active]:bg-[#E2705A] data-[state=active]:text-white">Video tiles</TabsTrigger>
            <TabsTrigger value="documents" data-testid="admin-tab-documents" className="font-type data-[state=active]:bg-[#E2705A] data-[state=active]:text-white">Documents</TabsTrigger>
            <TabsTrigger value="letter" data-testid="admin-tab-letter" className="font-type data-[state=active]:bg-[#E2705A] data-[state=active]:text-white">F.E.R.M letter</TabsTrigger>
          </TabsList>
          <TabsContent value="members" className="mt-6"><MembersTable /></TabsContent>
          <TabsContent value="mps" className="mt-6"><MPsManager /></TabsContent>
          <TabsContent value="emails" className="mt-6 space-y-6"><BroadcastComposer /><BroadcastHistory /><EmailSettings /><EmailLog /></TabsContent>
          <TabsContent value="meetings" className="mt-6"><MeetingsManager /></TabsContent>
          <TabsContent value="bills" className="mt-6"><BillsManager /></TabsContent>
          <TabsContent value="videos" className="mt-6"><VideosManager /></TabsContent>
          <TabsContent value="documents" className="mt-6 space-y-8"><DocumentsManager /><CardsManager /></TabsContent>
          <TabsContent value="letter" className="mt-6"><LetterManager /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
